import type { IdeaAlertRowViewModel, IdeaAlertRowStateChangeCallback } from '.';
import type { IdeaAlertRowAction } from './state/actions';
import { ideaAlertRowReducer } from './state/reducer';
import { createInitialIdeaAlertRowState, stateToNotifications, notificationsToState } from './state/types';
import type { IdeaAlertRowState, IdeaTriggerMode } from './state/types';
import { signal, effect, computed, untracked, batch } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { Universe } from '@thinkalpha/platform-ws-client/contracts/universe.js';
import { RowIdField } from '@thinkalpha/table-client';
import type { RowUpdateBatch } from '@thinkalpha/table-client';
import type { IdeaDTO } from 'src/dtos/Idea';
import { injectable, inject } from 'src/features/ioc';
import { buildResultsTableQueryOptions } from 'src/features/table/hooks/useResultsTableQueryOptions';
import { ensureColumns } from 'src/features/table/lib/ensureColumns';
import type { TableStatus } from 'src/features/table/types';
import { UniversePlanDTO } from 'src/features/universe/dtos/UniversePlanDTO';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { NEW_TABLE_RESET } from 'src/models/TableModel';
import type { TableModel } from 'src/models/TableModel';
import { getIdeaDTOByIdQuery } from 'src/queries/ideas';
import { getUniversesQuery } from 'src/queries/universes';
import type { AlertService } from 'src/services/AlertService';
import type { IdeaAlertModel } from 'src/services/AlertService/IdeaAlertModel';
import type { FullKeyType } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';
import { v4 } from 'uuid';

/**
 * Implementation of IdeaAlertRowViewModel.
 * Manages idea-based alert state and client-side monitoring via a bare table.
 */
@injectable()
export class IdeaAlertRowViewModelImpl implements IdeaAlertRowViewModel, Disposable {
    #ideaAlertModel: IdeaAlertModel | null = null;
    #id: string = '';

    readonly #state = signal<IdeaAlertRowState>(createInitialIdeaAlertRowState());
    readonly #isMonitoring = signal<boolean>(false);
    readonly #disposableStack = new DisposableStack();

    #initialized = false;
    #onStateChange: IdeaAlertRowStateChangeCallback | null = null;
    #suppressStateChangeCallback = false;

    // Current monitoring cleanup — replaced each time monitoring starts
    #stopCurrentMonitoring: (() => void) | null = null;

    // Row tracking for entry/exit detection
    readonly #rowIdToSymbolMap = new Map<number, string>();
    #symbolFieldId: string | undefined;
    #pendingReset = false;
    #firstBatchAfterStart = false;

    // Reference to the active monitoring table (drives matchCount computed)
    readonly #monitoringTable = signal<TableModel | null>(null);
    readonly #hasReceivedData = signal(false);

    // Convenience computed signals
    readonly enabled: ReadonlySignal<boolean>;
    readonly showToast: ReadonlySignal<boolean>;
    readonly playSound: ReadonlySignal<boolean>;
    readonly soundType: ReadonlySignal<string>;
    readonly speakTts: ReadonlySignal<boolean>;
    readonly ttsText: ReadonlySignal<string | undefined>;
    readonly ttsVoiceUri: ReadonlySignal<string | undefined>;
    readonly ttsRate: ReadonlySignal<number>;
    readonly ttsPitch: ReadonlySignal<number>;
    readonly ttsVolume: ReadonlySignal<number>;
    readonly note: ReadonlySignal<string | null>;
    readonly triggerMode: ReadonlySignal<IdeaTriggerMode>;
    readonly slowIdeaId: ReadonlySignal<string | null>;
    readonly fastIdeaId: ReadonlySignal<string | null>;
    readonly slowIdeaLabel: ReadonlySignal<string>;
    readonly fastIdeaLabel: ReadonlySignal<string | null>;

    // QueryObserverModels for reactive label resolution
    readonly #universesQuery: QueryObserverModel<Universe[]>;
    readonly #slowIdeaQuery: QueryObserverModel<IdeaDTO | null>;
    readonly #fastIdeaQuery: QueryObserverModel<IdeaDTO | null>;

    // matchCount and connectionStatus derive from the monitoring table
    readonly matchCount: ReadonlySignal<number | null>;
    readonly connectionStatus: ReadonlySignal<TableStatus>;

    constructor(
        @inject('TableModelFactory') private readonly tableModelFactory: ReactBindings['TableModelFactory'],
        @inject('AlertService') private readonly alertService: AlertService,
        @inject('QueryClient') private readonly queryClient: ReactBindings['QueryClient'],
        @inject('FormulaService') private readonly formulaService: ReactBindings['FormulaService'],
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('SlugMapServiceProvider')
        private readonly slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        @inject('SlugMapService') private readonly slugMapService: ReactBindings['SlugMapService'],
        @inject('Logger') private logger: ReactBindings['Logger'],
    ) {
        this.logger = logger.getSubLogger({ name: 'IdeaAlertRowViewModel' });

        this.matchCount = computed(() => {
            if (!this.#monitoringTable.value || !this.#hasReceivedData.value) return null;
            return this.#monitoringTable.value.countSignal.value;
        });

        this.connectionStatus = computed(() => this.#monitoringTable.value?.connectionStatus.value ?? 'uninitialized');

        this.enabled = computed(() => this.#state.value.enabled);
        this.showToast = computed(() => this.#state.value.showToast);
        this.playSound = computed(() => this.#state.value.playSound);
        this.soundType = computed(() => this.#state.value.soundType);
        this.speakTts = computed(() => this.#state.value.speakTts);
        this.ttsText = computed(() => this.#state.value.ttsText);
        this.ttsVoiceUri = computed(() => this.#state.value.ttsVoiceUri);
        this.ttsRate = computed(() => this.#state.value.ttsRate);
        this.ttsPitch = computed(() => this.#state.value.ttsPitch);
        this.ttsVolume = computed(() => this.#state.value.ttsVolume);
        this.note = computed(() => this.#state.value.note);
        this.triggerMode = computed(() => this.#state.value.triggerMode);
        this.slowIdeaId = computed(() => this.#state.value.slowIdeaId);
        this.fastIdeaId = computed(() => this.#state.value.fastIdeaId);

        // Reactive queries for label resolution
        this.#universesQuery = queryObserverModelFactory(getUniversesQuery());
        this.#disposableStack.use(this.#universesQuery);

        // Slow idea query: enabled only when slowIdeaId is set and not a universe
        this.#slowIdeaQuery = queryObserverModelFactory(
            computed(() => {
                const slowId = this.#state.value.slowIdeaId;
                const universes = this.#universesQuery.signal.value.data;
                const isUniverseId = slowId && universes ? universes.some((u) => u.id === slowId) : false;
                return {
                    ...getIdeaDTOByIdQuery(this.queryClient, this.formulaService, this.slugMapService, slowId ?? ''),
                    enabled: !!slowId && universes != null && !isUniverseId,
                };
            }),
        );
        this.#disposableStack.use(this.#slowIdeaQuery);

        // Fast idea query: enabled when fastIdeaId is set
        this.#fastIdeaQuery = queryObserverModelFactory(
            computed(() => ({
                ...getIdeaDTOByIdQuery(
                    this.queryClient,
                    this.formulaService,
                    this.slugMapService,
                    this.#state.value.fastIdeaId ?? '',
                ),
                enabled: !!this.#state.value.fastIdeaId,
            })),
        );
        this.#disposableStack.use(this.#fastIdeaQuery);

        // Derive labels from query data
        this.slowIdeaLabel = computed(() => {
            const slowId = this.#state.value.slowIdeaId;
            if (!slowId) return 'All Symbols';
            const universes = this.#universesQuery.signal.value.data;
            const universe = universes?.find((u) => u.id === slowId);
            if (universe) return universe.name;
            const idea = this.#slowIdeaQuery.signal.value.data;
            return idea?.idea?.name ?? '…';
        });

        this.fastIdeaLabel = computed(() => {
            const fastId = this.#state.value.fastIdeaId;
            if (!fastId) return null;
            const idea = this.#fastIdeaQuery.signal.value.data;
            return idea?.idea?.name ?? '…';
        });

        // Reactively start/stop monitoring when enabled / idea IDs / slug map change.
        // Tracking the slug-map signal here so that a subscription-change cache
        // invalidation (which transitions the slug map from `{}` → populated)
        // rebuilds the monitoring table's column template against the fresh
        // dictionary instead of leaving it pinned to the pre-subscription state.
        this.#disposableStack.defer(
            effect(() => {
                const enabled = this.enabled.value;
                const slowIdeaId = this.slowIdeaId.value;
                const fastIdeaId = this.fastIdeaId.value;
                void this.slugMapService.slugMap.value;
                if (enabled && (slowIdeaId !== null || fastIdeaId !== null)) {
                    void this.#startMonitoring(slowIdeaId, fastIdeaId);
                } else {
                    this.#tearDownMonitoring();
                }
            }),
        );

        this.#disposableStack.defer(() => this.#tearDownMonitoring());
    }

    get id(): string {
        return this.#id;
    }

    get ideaAlertModel(): IdeaAlertModel | null {
        return this.#ideaAlertModel;
    }

    get isMonitoring(): ReadonlySignal<boolean> {
        return this.#isMonitoring;
    }

    dispatch(action: IdeaAlertRowAction): void {
        const prevState = this.#state.value;
        const nextState = ideaAlertRowReducer(prevState, action);

        if (nextState === prevState) return;

        const isSyncAction = action.type === 'SYNC_FROM_DEFINITION';
        this.#state.value = nextState;

        if (!isSyncAction && this.#onStateChange && !this.#suppressStateChangeCallback) {
            this.#onStateChange(this.#id, nextState);
        }
    }

    setOnStateChange(cb: IdeaAlertRowStateChangeCallback): void {
        this.#onStateChange = cb;
    }

    initFromIdeaAlertModel(model: IdeaAlertModel): void {
        if (this.#initialized) {
            this.logger.warn('IdeaAlertRowViewModel already initialized');
            return;
        }
        this.#initialized = true;
        this.#ideaAlertModel = model;
        this.#id = model.id;

        // Sync initial state from definition
        this.#suppressStateChangeCallback = true;
        this.#syncFromDefinition(model.definition.value);
        this.#suppressStateChangeCallback = false;

        // React to future definition changes (server updates)
        let lastSyncedDef = model.definition.value;
        this.#disposableStack.defer(
            effect(() => {
                const def = model.definition.value;
                if (!def || def === lastSyncedDef) return;
                lastSyncedDef = def;
                this.#suppressStateChangeCallback = true;
                batch(() => {
                    this.dispatch({
                        type: 'SYNC_FROM_DEFINITION',
                        enabled: def.enabled,
                        ...notificationsToState(def.notifications),
                        note: def.userNote ?? null,
                        triggerMode: def.triggerMode,
                        slowIdeaId: def.slowIdeaId,
                        fastIdeaId: def.fastIdeaId,
                    });
                });
                this.#suppressStateChangeCallback = false;
            }),
        );
    }

    #syncFromDefinition(def: IdeaAlertModel['definition']['value']): void {
        if (!def) return;
        this.dispatch({
            type: 'SYNC_FROM_DEFINITION',
            enabled: def.enabled,
            ...notificationsToState(def.notifications),
            note: def.userNote ?? null,
            triggerMode: def.triggerMode,
            slowIdeaId: def.slowIdeaId,
            fastIdeaId: def.fastIdeaId,
        });
    }

    #tearDownMonitoring(): void {
        if (this.#stopCurrentMonitoring) {
            this.#stopCurrentMonitoring();
            this.#stopCurrentMonitoring = null;
        }
        this.#rowIdToSymbolMap.clear();
        this.#symbolFieldId = undefined;
        this.#pendingReset = false;
        this.#hasReceivedData.value = false;
        this.#monitoringTable.value = null;
        this.#isMonitoring.value = false;
    }

    async #startMonitoring(slowIdeaId: string | null, fastIdeaId: string | null): Promise<void> {
        this.#tearDownMonitoring();

        if (TRACE) {
            this.logger.trace('Starting idea alert monitoring', { alertId: this.#id, slowIdeaId, fastIdeaId });
        }

        let cancelled = false;
        let table: TableModel | null = null;

        this.#stopCurrentMonitoring = () => {
            cancelled = true;
            table?.[Symbol.dispose]();
            table = null;
            if (TRACE) {
                this.logger.trace('Stopped idea alert monitoring', { alertId: this.#id });
            }
        };

        try {
            // Resolve IdeaDTOs from IDs.
            // slowIdeaId may be either a screener (idea) ID or a universe ID — check universes first.
            let slowIdea: IdeaDTO | undefined;
            let fastIdea: IdeaDTO | undefined;

            if (slowIdeaId) {
                const universes = await this.queryClient.fetchQuery(getUniversesQuery());
                const isUniverseId = universes?.some((u) => u.id === slowIdeaId);
                if (isUniverseId) {
                    slowIdea = UniversePlanDTO.fromUniverseId(slowIdeaId).toTempIdea();
                } else {
                    slowIdea = await this.queryClient.fetchQuery(
                        getIdeaDTOByIdQuery(this.queryClient, this.formulaService, this.slugMapService, slowIdeaId),
                    );
                }
            }
            if (fastIdeaId) {
                fastIdea = await this.queryClient.fetchQuery(
                    getIdeaDTOByIdQuery(this.queryClient, this.formulaService, this.slugMapService, fastIdeaId),
                );
            }

            if (cancelled) return;

            // Verify state hasn't changed
            const currentState = untracked(() => this.#state.value);
            if (
                !currentState.enabled ||
                currentState.slowIdeaId !== slowIdeaId ||
                currentState.fastIdeaId !== fastIdeaId
            ) {
                return;
            }

            // Build column template with explicit symbol column
            const slugMapService = await this.slugMapServiceProvider();
            const monitoringColumns = ensureColumns(slugMapService.slugMap.value, [], [Slugs.symbol]);

            if (cancelled) return;

            const cacheKey = v4();
            const queryOptions = buildResultsTableQueryOptions({
                slowIdea,
                fastIdeas: fastIdea ? [fastIdea] : [],
                validationModeEnabled: false,
                columnTemplate: { columns: monitoringColumns },
                useScrolling: false,
                userViewKey: undefined,
                ideaUserViewKeyScope: 'none',
                columnOptions: {},
                log: this.logger,
                fastIdeasCacheKey: cacheKey,
                slowIdeaCacheKey: cacheKey,
                columnTemplateRunKey: 'monitoring',
                sort: undefined,
            });

            if (cancelled) return;

            const tableQueryOptions = {
                ...queryOptions,
                select: (data: [unknown, unknown]) => data[0] as FullKeyType,
            };

            table = this.tableModelFactory();
            table.init({
                initBounds: { firstRow: 0, windowSize: 100000 },
                devtoolsName: `IdeaAlert/${this.#id}`,
                queryOptions: tableQueryOptions,
            });

            this.#monitoringTable.value = table;
            this.#firstBatchAfterStart = true;
            this.#isMonitoring.value = true;

            // Resolve symbol column field ID once columns are available
            const unsubColumns = table.columnsSignal.subscribe((columns) => {
                if (!columns?.length) return;
                this.#symbolFieldId = columns.find((c) => c.matchSlug === Slugs.symbol)?.id;
            });

            const updateSub = table.updatesWithReset$.subscribe({
                next: (batch) => this.#handleTableUpdate(batch),
                error: (err) => this.logger.error('Monitoring table error', { alertId: this.#id, error: err }),
            });

            const prevStop = this.#stopCurrentMonitoring;
            this.#stopCurrentMonitoring = () => {
                prevStop?.();
                unsubColumns();
                updateSub.unsubscribe();
            };
        } catch (error) {
            if (!cancelled) {
                this.logger.error('Failed to start idea alert monitoring', { alertId: this.#id, error });
                this.#isMonitoring.value = false;
            }
        }
    }

    #handleTableUpdate(batch: RowUpdateBatch | NEW_TABLE_RESET): void {
        if (batch === NEW_TABLE_RESET) {
            this.#pendingReset = true;
            return;
        }

        this.#hasReceivedData.value = true;

        // Snapshots and first-batch-after-reset use reconciliation; normal diffs use incremental processing
        const useReconciliation = batch.type === 'snapshot' || this.#pendingReset;
        if (this.#pendingReset) this.#pendingReset = false;

        if (useReconciliation) {
            this.#reconcile(batch);
        } else if (batch.type === 'diff') {
            this.#processDiff(batch);
        }
    }

    /**
     * Full reconciliation: compare incoming row IDs against existing map to find additions and deletions.
     * Used for snapshots and first batch after a server reset.
     */
    #reconcile(batch: RowUpdateBatch): void {
        const incomingMap = new Map<number, string>();
        for (const row of batch) {
            const rowId = row[RowIdField];
            if (rowId === undefined) continue;
            const symbolName = this.#symbolFieldId ? String(row[this.#symbolFieldId] ?? 'Unknown') : 'Unknown';
            incomingMap.set(rowId, symbolName);
        }

        // Suppress notifications on the very first batch after monitoring starts
        const suppress = this.#firstBatchAfterStart;
        if (this.#firstBatchAfterStart) this.#firstBatchAfterStart = false;

        if (!suppress) {
            const triggerMode = untracked(() => this.#state.value.triggerMode);
            const notifications = stateToNotifications(untracked(() => this.#state.value));
            const ideaName = untracked(() => this.fastIdeaLabel.value ?? this.slowIdeaLabel.value);

            // Exits: rows we knew about but are no longer present
            for (const [rowId, symbolName] of this.#rowIdToSymbolMap) {
                if (!incomingMap.has(rowId)) {
                    if (triggerMode === 'exit' || triggerMode === 'all') {
                        this.alertService.fireIdeaAlertNotification(
                            this.#id,
                            `Exit: ${symbolName} \u2014 ${ideaName}`,
                            notifications,
                            { symbol: symbolName, event: 'exit', ideaName },
                        );
                    }
                }
            }

            // Entries: rows in incoming that we didn't know about
            for (const [rowId, symbolName] of incomingMap) {
                if (!this.#rowIdToSymbolMap.has(rowId)) {
                    if (triggerMode === 'enter' || triggerMode === 'all') {
                        this.alertService.fireIdeaAlertNotification(
                            this.#id,
                            `Entry: ${symbolName} \u2014 ${ideaName}`,
                            notifications,
                            { symbol: symbolName, event: 'enter', ideaName },
                        );
                    }
                }
            }
        }

        // Replace tracking map with incoming state
        this.#rowIdToSymbolMap.clear();
        for (const [rowId, symbolName] of incomingMap) {
            this.#rowIdToSymbolMap.set(rowId, symbolName);
        }
    }

    /**
     * Incremental diff processing: use deletedRowIds for exits, new rows for entries.
     */
    #processDiff(batch: RowUpdateBatch): void {
        if (batch.type !== 'diff') return;

        // Suppress notifications on the very first batch after monitoring starts
        const suppress = this.#firstBatchAfterStart;
        if (this.#firstBatchAfterStart) this.#firstBatchAfterStart = false;

        const triggerMode = untracked(() => this.#state.value.triggerMode);
        const notifications = stateToNotifications(untracked(() => this.#state.value));
        const ideaName = untracked(() => this.fastIdeaLabel.value ?? this.slowIdeaLabel.value);

        for (const deletedId of batch.deletedRowIds) {
            const symbolName = this.#rowIdToSymbolMap.get(deletedId) ?? 'Unknown';
            this.#rowIdToSymbolMap.delete(deletedId);

            if (!suppress && (triggerMode === 'exit' || triggerMode === 'all')) {
                this.alertService.fireIdeaAlertNotification(
                    this.#id,
                    `Exit: ${symbolName} \u2014 ${ideaName}`,
                    notifications,
                    { symbol: symbolName, event: 'exit', ideaName },
                );
            }
        }

        for (const row of batch) {
            const rowId = row[RowIdField];
            if (rowId === undefined) continue;

            const symbolName = this.#symbolFieldId ? String(row[this.#symbolFieldId] ?? 'Unknown') : 'Unknown';
            const isNew = !this.#rowIdToSymbolMap.has(rowId);

            this.#rowIdToSymbolMap.set(rowId, symbolName);

            if (!suppress && isNew && (triggerMode === 'enter' || triggerMode === 'all')) {
                this.alertService.fireIdeaAlertNotification(
                    this.#id,
                    `Entry: ${symbolName} \u2014 ${ideaName}`,
                    notifications,
                    { symbol: symbolName, event: 'enter', ideaName },
                );
            }
        }
    }

    readonly [Symbol.dispose] = () => {
        this.#tearDownMonitoring();
        this.#disposableStack.dispose();
    };
}
